import React from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../../styles/colors';
import { typography } from '../../styles/typography';
import { spacing } from '../../styles/spacing';

// Card variants
const cardVariants = {
  default: css`
    background: ${colors.background.primary};
    border: 1px solid ${colors.border.primary};
  `,
  elevated: css`
    background: ${colors.background.primary};
    border: none;
    box-shadow: ${colors.shadows.lg};
  `,
  outlined: css`
    background: transparent;
    border: 1px solid ${colors.border.primary};
  `,
  filled: css`
    background: ${colors.background.secondary};
    border: none;
  `,
  gradient: css`
    background: ${colors.gradients.primary};
    border: none;
  `,
};

// Card sizes
const cardSizes = {
  sm: css`
    padding: ${spacing[4]};
    border-radius: ${spacing.radius.lg};
  `,
  md: css`
    padding: ${spacing[6]};
    border-radius: ${spacing.radius.xl};
  `,
  lg: css`
    padding: ${spacing[8]};
    border-radius: ${spacing.radius['2xl']};
  `,
  xl: css`
    padding: ${spacing[10]};
    border-radius: ${spacing.radius['3xl']};
  `,
};

// Styled Card component
const StyledCard = styled.div<{
  $variant: keyof typeof cardVariants;
  $size: keyof typeof cardSizes;
  $interactive?: boolean;
}>`
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  
  ${props => cardVariants[props.$variant]}
  ${props => cardSizes[props.$size]}
  
  ${props => props.$interactive && css`
    cursor: pointer;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${colors.shadows.xl};
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
`;

// Card Header
const CardHeader = styled.div`
  margin-bottom: ${spacing[4]};
`;

// Card Title
const CardTitle = styled.h3`
  font-family: ${typography.fonts.primary};
  font-size: ${typography.sizes.lg};
  font-weight: ${typography.weights.semibold};
  color: ${colors.text.primary};
  margin: 0 0 ${spacing[2]} 0;
  line-height: ${typography.lineHeights.snug};
`;

// Card Description
const CardDescription = styled.p`
  font-family: ${typography.fonts.primary};
  font-size: ${typography.sizes.sm};
  color: ${colors.text.secondary};
  margin: 0;
  line-height: ${typography.lineHeights.relaxed};
`;

// Card Content
const CardContent = styled.div`
  margin-bottom: ${spacing[4]};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// Card Footer
const CardFooter = styled.div`
  margin-top: ${spacing[4]};
  padding-top: ${spacing[4]};
  border-top: 1px solid ${colors.border.primary};
`;

// Card props interface
interface CardProps {
  variant?: keyof typeof cardVariants;
  size?: keyof typeof cardSizes;
  interactive?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

// Card sub-components
interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface CardTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface CardDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

// Main Card component
const Card: React.FC<CardProps> & {
  Header: React.FC<CardHeaderProps>;
  Title: React.FC<CardTitleProps>;
  Description: React.FC<CardDescriptionProps>;
  Content: React.FC<CardContentProps>;
  Footer: React.FC<CardFooterProps>;
} = ({
  variant = 'default',
  size = 'md',
  interactive = false,
  children,
  className,
  onClick,
}) => {
  return (
    <StyledCard
      $variant={variant}
      $size={size}
      $interactive={interactive}
      className={className}
      onClick={onClick}
    >
      {children}
    </StyledCard>
  );
};

// Card sub-components
Card.Header = ({ children, className }) => (
  <CardHeader className={className}>{children}</CardHeader>
);

Card.Title = ({ children, className }) => (
  <CardTitle className={className}>{children}</CardTitle>
);

Card.Description = ({ children, className }) => (
  <CardDescription className={className}>{children}</CardDescription>
);

Card.Content = ({ children, className }) => (
  <CardContent className={className}>{children}</CardContent>
);

Card.Footer = ({ children, className }) => (
  <CardFooter className={className}>{children}</CardFooter>
);

export default Card;